import PhotoSwipeLightbox from "./photoswipe-lightbox.esm.min.js";

class ZoomAction extends HTMLElement {
  constructor() {
    super();
    this.lightbox = null;
  }

  connectedCallback() {
    this.initLightBox();
  }

  initLightBox() {
    if (this.dataset.type !== "open_lightbox" || this.lightbox) return;

    // The gallery element survives color switches; only its image links change.
    // PhotoSwipe resolves the current links when an image is opened.
    const lightbox = new PhotoSwipeLightbox({
      gallery: this,
      children: "a.media-gallery__image",
      // The blur placeholder is scaled beyond the visible image bounds.
      thumbSelector: "img:not(.ds-responsive-image__placeholder)",
      pswpModule: () => import("./photoswipe.esm.min.js"),
      counter: false,
      zoom: false,
      preloader: false,
      arrowPrev: false,
      arrowNext: false,
      close: false,
      loop: false,
      bgOpacity: 1,
      paddingFn: (viewportSize) => {
        // Let mobile images use the full viewport width while retaining vertical breathing room.
        if (viewportSize.x < 768 || window.matchMedia("(pointer: coarse)").matches) {
          return { top: 16, right: 0, bottom: 16, left: 0 };
        }
        return { top: 40, right: 40, bottom: 40, left: 40 };
      },
    });

    lightbox.addFilter("domItemData", (itemData, element, linkEl) => {
      // Use the loaded image, not the 20px blur placeholder, for the opening transition.
      const image = (linkEl || element).querySelector("img:not(.ds-responsive-image__placeholder)");
      if (image) {
        itemData.alt = image.alt;
        if (image.complete && image.naturalWidth > 0) {
          itemData.msrc = image.currentSrc || image.src;
        }
      }
      return itemData;
    });

    lightbox.on("close", () => {
      // Align the viewed slide before PhotoSwipe measures its closing destination.
      // Hidden Swiper slides sit offscreen and would send the image sideways.
      const imageLink = lightbox.pswp?.currSlide?.data.element;
      this.closest("media-gallery")?.showMediaFromLightbox?.(imageLink);
    });

    lightbox.on("destroy", () => {
      // PhotoSwipe restores focus to the opening link first, which can move Swiper back.
      const pswp = lightbox.pswp;
      const imageLink = pswp?.getItemData(pswp.potentialIndex)?.element;
      this.closest("media-gallery")?.showMediaFromLightbox?.(imageLink);
    });

    lightbox.on("uiRegister", () => {
      const { pswp } = lightbox;
      let closeQueued = false;
      pswp.ui.registerElement({
        name: "gallery-close",
        className: "product-lightbox__button product-lightbox__close",
        appendTo: "root",
        isButton: true,
        tagName: "button",
        ariaLabel: "Close gallery",
        html: '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false"><use href="#icon-close"></use></svg>',
        onClick: () => {
          // PhotoSwipe ignores close() while its opening animation is running.
          if (!pswp.opener?.isOpening) {
            pswp.close();
            return;
          }
          if (closeQueued) return;
          closeQueued = true;
          const closeAfterOpening = () => {
            pswp.off("openingAnimationEnd", closeAfterOpening);
            pswp.close();
          };
          pswp.on("openingAnimationEnd", closeAfterOpening);
        },
      });
      pswp.ui.registerElement({
        name: "gallery-counter",
        className: "product-lightbox__counter",
        appendTo: "root",
        onInit: (el, gallery) => {
          const update = () => {
            el.textContent = `${gallery.currIndex + 1} / ${gallery.getNumItems()}`;
          };
          gallery.on("change", update);
          update();
        },
      });
      pswp.ui.registerElement({
        name: "gallery-prev",
        className: "product-lightbox__button product-lightbox__arrow product-lightbox__arrow--prev",
        appendTo: "root",
        isButton: true,
        tagName: "button",
        ariaLabel: "Previous image",
        html: '<svg width="28" height="28" viewBox="0 0 1024 1024" aria-hidden="true" focusable="false"><use href="#ic-arrow-left"></use></svg>',
        onClick: () => pswp.prev(),
      });
      pswp.ui.registerElement({
        name: "gallery-next",
        className: "product-lightbox__button product-lightbox__arrow product-lightbox__arrow--next",
        appendTo: "root",
        isButton: true,
        tagName: "button",
        ariaLabel: "Next image",
        html: '<svg width="28" height="28" viewBox="0 0 1024 1024" aria-hidden="true" focusable="false"><use href="#ic-arrow-right"></use></svg>',
        onClick: () => pswp.next(),
      });
    });

    lightbox.init();
    this.lightbox = lightbox;
  }

  disconnectedCallback() {
    this.lightbox?.destroy();
    this.lightbox = null;
  }
}

if (!customElements.get("zoom-action")) {
  customElements.define("zoom-action", ZoomAction);
}
